'use client';

import { Badge } from '@get-mosh/msh-shared';
import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { SliceComponentProps } from '@prismicio/react';
import classNames from 'classnames';
import { useState } from 'react';

/**
 * Props for `ReviewsTab`.
 */
export type ReviewsTabProps = SliceComponentProps<Content.ReviewsTabSlice>;

/**
 * Component for "ReviewsTab" Slices.
 */
const ReviewsTab = ({ slice }: ReviewsTabProps): JSX.Element => {
    const [selectedTab, setSelectedTab] = useState(1);

    return (
        <section
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
            className="container mx-auto px-4"
        >
            <div className="flex flex-col items-center gap-8 py-16">
                {/* Title */}
                <div className="flex justify-center text-center">
                    <h2>{slice.primary.title}</h2>
                </div>

                {/* Tabs Section */}
                <div className="hide-scrollbar flex w-full overflow-x-auto lg:justify-center">
                    <div className="flex flex-grow justify-evenly pl-4">
                        {Array.from({
                            length: Number(slice.primary.amount_of_tab_groups),
                        }).map((_, i) => (
                            <div
                                key={i}
                                className={classNames(
                                    'flex flex-grow cursor-pointer items-center justify-center gap-2 px-6 py-3',
                                    {
                                        'border-b-2 border-blue font-bold':
                                            selectedTab === i + 1,
                                        'border-b-2 border-grey-100':
                                            selectedTab !== i + 1,
                                    },
                                )}
                                onClick={() => setSelectedTab(i + 1)}
                            >
                                {slice.primary[`tab_${i + 1}_image`] && (
                                    <PrismicNextImage
                                        field={
                                            slice.primary[`tab_${i + 1}_image`]
                                        }
                                        width="40"
                                        height="40"
                                    />
                                )}
                                {slice.primary[`tab_${i + 1}`]}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Before and After Cards */}
                <div className="flex w-full gap-4 overflow-x-auto pb-4">
                    <div className="flex min-w-max gap-4">
                        {slice.primary[
                            `before_after_card_tab_${selectedTab}`
                        ]?.map((item, idx) => (
                            <div
                                key={idx}
                                className="flex w-[288px] flex-col items-start gap-3 rounded-2xl bg-grey-100 p-4 pr-5 lg:w-[480px]"
                            >
                                <div className="flex gap-1">
                                    <PrismicNextImage
                                        field={item.before_image}
                                        className="aspect-[3/4] max-w-[50%] flex-1 rounded-bl-lg rounded-tl-lg object-cover"
                                    />
                                    <PrismicNextImage
                                        field={item.after_image}
                                        className="aspect-[3/4] max-w-[50%] flex-1 rounded-bl-lg rounded-tl-lg object-cover"
                                    />
                                </div>
                                <div className="flex w-full justify-between text-green-dark">
                                    <h4 className="font-bold">{item.title}</h4>
                                    <h4 className="font-body">
                                        {item?.description}
                                    </h4>
                                </div>
                                <div className="flex max-w-full flex-wrap gap-2 text-xs font-bold text-white">
                                    {item.tag_1 ? (
                                        <Badge className="!bg-purple !text-purple-dark">
                                            {item.tag_1}
                                        </Badge>
                                    ) : null}
                                    {item.tag_2 ? (
                                        <Badge className="!bg-purple !text-purple-dark">
                                            {item.tag_2}
                                        </Badge>
                                    ) : null}
                                    {item.tag_3 ? (
                                        <Badge className="!bg-purple !text-purple-dark">
                                            {item.tag_3}
                                        </Badge>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <p className="text-center text-grey-400">
                        {slice.primary.disclaimer}
                    </p>
                </div>
            </div>
        </section>
    );
};

export default ReviewsTab;
